// src/LoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchJson } from './api';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@300;400;500;600;700;800;900&family=DM+Serif+Display:ital@0;1&family=Space+Mono:wght@400;700&display=swap');
  .jkl-login-root *,.jkl-login-root *::before,.jkl-login-root *::after{box-sizing:border-box;}
  .jkl-login-root{--black:#07070a;--deep:#0a0a0f;--card:#0f0f16;--border:rgba(255,255,255,0.07);--gold:#d4a550;--gold-lt:#f0c878;--gold-dim:rgba(212,165,80,0.10);--white:#f0ece4;--muted:#8a8a9a;--faint:#2a2a38;--error:#f87171;min-height:100vh;background:var(--black);color:var(--white);font-family:'Kanit',sans-serif;display:grid;grid-template-columns:1fr 1fr;position:relative;}
  .jkl-login-root::before{content:'';position:fixed;inset:0;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");pointer-events:none;z-index:0;opacity:0.4;}
  .jkl-left{background:var(--deep);border-right:1px solid var(--border);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px;position:relative;overflow:hidden;}
  .jkl-left::before{content:'';position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:500px;height:500px;background:radial-gradient(ellipse,rgba(212,165,80,0.07) 0%,transparent 65%);pointer-events:none;}
  .jkl-left::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(212,165,80,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(212,165,80,0.04) 1px,transparent 1px);background-size:48px 48px;pointer-events:none;}
  .jkl-left-content{position:relative;z-index:1;text-align:center;}
  .jkl-keys{display:flex;align-items:center;justify-content:center;gap:10px;margin-bottom:40px;}
  .jkl-key{width:64px;height:64px;border:1px solid rgba(212,165,80,0.25);border-radius:8px;display:flex;flex-direction:column;align-items:center;justify-content:center;background:rgba(212,165,80,0.03);box-shadow:inset 0 1px 0 rgba(255,255,255,0.04);animation:jklKeyFloat 3s ease-in-out infinite;}
  .jkl-key:nth-child(1){animation-delay:0s;}.jkl-key:nth-child(3){animation-delay:0.3s;}.jkl-key:nth-child(5){animation-delay:0.6s;}
  @keyframes jklKeyFloat{0%,100%{transform:translateY(0);}50%{transform:translateY(-6px);}}
  .jkl-key-letter{font-family:'Kanit',sans-serif;font-size:26px;font-weight:900;color:#f0ece4;line-height:1;margin-bottom:2px;}
  .jkl-key-sub{font-family:'Space Mono',monospace;font-size:7px;color:#d4a550;letter-spacing:0.05em;}
  .jkl-key-sep{font-family:'Space Mono',monospace;font-size:16px;color:#2a2a38;}
  .jkl-tagline{font-family:'DM Serif Display',serif;font-size:32px;font-style:italic;line-height:1.2;color:#f0ece4;margin-bottom:16px;}
  .jkl-tagline em{font-style:normal;color:#d4a550;}
  .jkl-sub{font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.15em;color:#8a8a9a;text-transform:uppercase;}
  .jkl-corner{position:absolute;font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.1em;color:rgba(212,165,80,0.2);}
  .jkl-corner.tl{top:24px;left:24px;}.jkl-corner.br{bottom:24px;right:24px;}
  .jkl-right{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:60px 72px;background:var(--black);overflow-y:auto;position:relative;z-index:1;}
  .jkl-form-wrap{width:100%;max-width:380px;animation:jklFadeUp 0.6s ease forwards;}
  @keyframes jklFadeUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
  .jkl-form-logo{font-family:'Kanit',sans-serif;font-size:20px;font-weight:900;letter-spacing:0.2em;text-transform:uppercase;color:#f0ece4;margin-bottom:8px;}
  .jkl-form-eyebrow{font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.15em;color:#d4a550;text-transform:uppercase;margin-bottom:40px;}
  .jkl-form-title{font-family:'Kanit',sans-serif;font-size:28px;font-weight:800;text-transform:uppercase;letter-spacing:-0.01em;margin-bottom:32px;line-height:1;}
  .jkl-field{margin-bottom:20px;}
  .jkl-field label{display:block;font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.18em;color:#8a8a9a;text-transform:uppercase;margin-bottom:8px;}
  .jkl-field input{width:100%;padding:14px 16px;background:#0f0f16;border:1px solid rgba(255,255,255,0.07);border-radius:6px;color:#f0ece4;font-family:'Kanit',sans-serif;font-size:15px;font-weight:400;outline:none;transition:border-color 0.2s,background 0.2s;}
  .jkl-field input::placeholder{color:#2a2a38;}
  .jkl-field input:focus{border-color:rgba(212,165,80,0.5);background:#12121a;}
  .jkl-field input.jkl-error{border-color:#f87171;}
  .jkl-pwd-wrap{position:relative;}.jkl-pwd-wrap input{padding-right:44px;}
  .jkl-toggle-pwd{position:absolute;right:14px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:#8a8a9a;font-size:14px;transition:color 0.2s;padding:4px;}
  .jkl-toggle-pwd:hover{color:#d4a550;}
  .jkl-form-error{background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.25);border-radius:6px;padding:12px 16px;font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.05em;color:#f87171;margin-bottom:20px;}
  .jkl-btn{width:100%;padding:15px;background:#d4a550;border:none;border-radius:6px;color:#07070a;font-family:'Kanit',sans-serif;font-size:14px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;margin-bottom:24px;display:flex;align-items:center;justify-content:center;gap:8px;}
  .jkl-btn:hover:not(:disabled){background:#f0c878;transform:translateY(-1px);box-shadow:0 6px 24px rgba(212,165,80,0.25);}
  .jkl-btn:disabled{opacity:0.5;cursor:not-allowed;}
  .jkl-divider{display:flex;align-items:center;gap:12px;margin-bottom:20px;}
  .jkl-divider::before,.jkl-divider::after{content:'';flex:1;height:1px;background:rgba(255,255,255,0.07);}
  .jkl-divider span{font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.1em;color:#2a2a38;text-transform:uppercase;white-space:nowrap;}
  .jkl-link-row{text-align:center;font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.08em;color:#8a8a9a;text-transform:uppercase;}
  .jkl-link-row button{background:none;border:none;cursor:pointer;color:#d4a550;font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;transition:color 0.2s;padding:0;}
  .jkl-link-row button:hover{color:#f0c878;}
  .jkl-spinner{width:16px;height:16px;border:2px solid rgba(0,0,0,0.3);border-top-color:#07070a;border-radius:50%;animation:jklSpin 0.7s linear infinite;}
  @keyframes jklSpin{to{transform:rotate(360deg);}}
  @media(max-width:768px){.jkl-login-root{grid-template-columns:1fr;}.jkl-left{display:none;}.jkl-right{padding:60px 32px;min-height:100vh;}}
`;

export default function LoginPage({ onLoginSuccess, onGoRegister, onForgotPassword }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!document.getElementById('jkl-login-styles')) {
      const style = document.createElement('style');
      style.textContent = STYLES;
      style.id = 'jkl-login-styles';
      document.head.appendChild(style);
    }
    return () => { document.getElementById('jkl-login-styles')?.remove(); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !/\S+@\S+\.\S+/.test(email)) return setError(t('login.err_email'));
    if (!password) return setError(t('login.err_password'));
    setLoading(true);
    try {
      const data = await fetchJson('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      localStorage.setItem('token', data.token);
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      if (data.user?.user_type) localStorage.setItem('jkl_user_type', data.user.user_type);
      onLoginSuccess?.(data);
    } catch (err) {
      setError(err.message || t('login.err_generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="jkl-login-root">
      <div className="jkl-left">
        <div className="jkl-corner tl">{t('login.corner')}</div>
        <div className="jkl-corner br">joinjkl.com</div>
        <div className="jkl-left-content">
          <div className="jkl-keys">
            <div className="jkl-key"><span className="jkl-key-letter">J</span><span className="jkl-key-sub">&larr; Back</span></div>
            <span className="jkl-key-sep">/</span>
            <div className="jkl-key"><span className="jkl-key-letter">K</span><span className="jkl-key-sub">&#9632; Stop</span></div>
            <span className="jkl-key-sep">/</span>
            <div className="jkl-key"><span className="jkl-key-letter">L</span><span className="jkl-key-sub">&rarr; Fwd</span></div>
          </div>
          <p className="jkl-tagline" dangerouslySetInnerHTML={{ __html: t('login.tagline') }} />
          <p className="jkl-sub">{t('login.sub')}</p>
        </div>
      </div>

      <div className="jkl-right">
        <div className="jkl-form-wrap">
          <div className="jkl-form-logo">JKL</div>
          <div className="jkl-form-eyebrow">{t('login.eyebrow')}</div>
          <h1 className="jkl-form-title">{t('login.title')}</h1>

          {error && <div className="jkl-form-error">{error}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="jkl-field">
              <label>{t('login.label_email')}</label>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder={t('login.ph_email')} autoComplete="email" className={error && !email ? 'jkl-error' : ''} />
            </div>
            <div className="jkl-field">
              <label>{t('login.label_password')}</label>
              <div className="jkl-pwd-wrap">
                <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setError(''); }} placeholder="••••••••" autoComplete="current-password" />
                <button type="button" className="jkl-toggle-pwd" onClick={() => setShowPwd(v => !v)}>{showPwd ? '🙈' : '👁'}</button>
              </div>
            </div>
            <button type="submit" className="jkl-btn" disabled={loading}>
              {loading ? <span className="jkl-spinner" /> : t('login.submit')}
            </button>
          </form>

          {onForgotPassword && (
            <div className="jkl-link-row" style={{ marginBottom: '20px' }}>
              <button onClick={onForgotPassword}>{t('login.forgot')}</button>
            </div>
          )}

          <div className="jkl-divider"><span>{t('login.no_account')}</span></div>
          <div className="jkl-link-row">
            <button onClick={onGoRegister}>{t('login.register')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
